import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit3, 
  CheckCircle2, 
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  Info,
  Heart,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import { 
  PRIORITY_LABELS, 
  PRIORITY_LEVELS,
  getTypesForMap,
  getSubtypesForType,
  typeHasSubtypes,
  getTypeLabel,
  getSubtypeLabel
} from '../store/mockData';
import { toast } from 'sonner';

const MapDetail = () => {
  const { mapId } = useParams();
  const { 
    getMapById, 
    getEntriesForMap, 
    getMapNotes, 
    updateMapNotes,
    addEntry,
    updateEntry,
    deleteEntry,
    updateMapStatus,
    getMapWishes,
    updateMapWishes,
    contacts,
    getContactById,
    fetchEntriesForMap,
    fetchContacts,
    isAuthenticated,
    loading
  } = useLifely();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch entries for this map when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && mapId && !dataLoaded) {
        try {
          await Promise.all([
            fetchEntriesForMap(mapId),
            fetchContacts()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load map data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, mapId, dataLoaded, fetchEntriesForMap, fetchContacts]);

  const map = getMapById(mapId);
  const entries = getEntriesForMap(mapId);
  const notes = getMapNotes(mapId);
  const mapWishes = getMapWishes ? getMapWishes(mapId) : null;

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(notes);
  const [isEntryDialogOpen, setIsEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showWishesDisclaimer, setShowWishesDisclaimer] = useState(false);
  
  // Map-level wishes state
  const [isEditingWishes, setIsEditingWishes] = useState(false);
  const [mapWishesContactId, setMapWishesContactId] = useState(mapWishes?.contactId || '');
  const [mapWishesNotes, setMapWishesNotes] = useState(mapWishes?.notes || '');
  
  // Entry-level wishes state
  const [wishesOverride, setWishesOverride] = useState(false);
  const [entryWishesContactId, setEntryWishesContactId] = useState('');
  const [entryWishesNotes, setEntryWishesNotes] = useState('');
  
  // Form state - Common fields
  const [entryLabel, setEntryLabel] = useState('');
  const [entryType, setEntryType] = useState('not-specified');
  const [entrySubtype, setEntrySubtype] = useState('not-specified');
  const [entryLocation, setEntryLocation] = useState('');
  const [entryPriority, setEntryPriority] = useState('medium');
  const [entryNotes, setEntryNotes] = useState('');
  const [entryFirstAction, setEntryFirstAction] = useState('');
  
  // Crypto-specific fields
  const [selfCustody, setSelfCustody] = useState(false);
  
  // Shares-specific fields
  const [tickerSymbol, setTickerSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [broker, setBroker] = useState('');
  const [accountReference, setAccountReference] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [documentation, setDocumentation] = useState('');

  // Property-specific fields
  const [propertyAddress, setPropertyAddress] = useState('');
  const [ownershipStatus, setOwnershipStatus] = useState('not-specified');
  const [deedLocation, setDeedLocation] = useState('');
  const [mortgageProvider, setMortgageProvider] = useState('');
  const [solicitorContact, setSolicitorContact] = useState('');

  // Vehicle-specific fields
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [vehicleVin, setVehicleVin] = useState('');
  const [vehicleOwnershipStatus, setVehicleOwnershipStatus] = useState('not-specified');
  const [sharedOwnership, setSharedOwnership] = useState(false);
  const [storageLocation, setStorageLocation] = useState('');
  const [insuranceDetails, setInsuranceDetails] = useState('');
  const [serviceContact, setServiceContact] = useState('');

  // Pet-specific fields
  const [petBreed, setPetBreed] = useState('');
  const [petDateOfBirth, setPetDateOfBirth] = useState('');
  const [vetContact, setVetContact] = useState('');
  const [medicalNeeds, setMedicalNeeds] = useState('');
  const [petOwnershipArrangements, setPetOwnershipArrangements] = useState('');
  const [verbalArrangements, setVerbalArrangements] = useState(false);
  const [emergencyCaregiver, setEmergencyCaregiver] = useState('');

  // Social-specific fields
  const [platformName, setPlatformName] = useState('');
  const [accountUrl, setAccountUrl] = useState('');
  const [accountDisposition, setAccountDisposition] = useState('not-specified');

  // Art-specific fields
  const [artistCreator, setArtistCreator] = useState('');
  const [yearPeriod, setYearPeriod] = useState('');
  const [mediumMaterials, setMediumMaterials] = useState('');
  const [provenance, setProvenance] = useState('');
  const [appraisedValue, setAppraisedValue] = useState('');
  const [appraisalDate, setAppraisalDate] = useState('');
  const [artStorageLocation, setArtStorageLocation] = useState('');
  const [artInsurance, setArtInsurance] = useState('');
  const [authentication, setAuthentication] = useState('');
  const [intendedDisposition, setIntendedDisposition] = useState('not-specified');

  // Physical Records-specific fields
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [documentFormat, setDocumentFormat] = useState('not-specified');
  const [accessInfo, setAccessInfo] = useState('');
  const [relatedTo, setRelatedTo] = useState('');
  const [retentionNotes, setRetentionNotes] = useState('');

  // Insurance-specific fields
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [policyReference, setPolicyReference] = useState('');
  const [coverageAmount, setCoverageAmount] = useState('');
  const [premiumDetails, setPremiumDetails] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [beneficiaries, setBeneficiaries] = useState('');
  const [agentContact, setAgentContact] = useState('');
  const [policyDocLocation, setPolicyDocLocation] = useState('');
  const [coverageDescription, setCoverageDescription] = useState('');

  // Sort state
  const [sortBy, setSortBy] = useState('date-newest');

  // Sort options - crypto has an extra option for self-custody
  const sortOptions = useMemo(() => {
    const baseOptions = [
      { value: 'name-asc', label: 'Name A → Z' },
      { value: 'name-desc', label: 'Name Z → A' },
      { value: 'date-oldest', label: 'Date added (oldest first)' },
      { value: 'date-newest', label: 'Date added (newest first)' },
      { value: 'priority-low', label: 'Priority (low → critical)' },
      { value: 'priority-high', label: 'Priority (critical → low)' },
    ];
    
    if (mapId === 'crypto') {
      baseOptions.push({ value: 'self-custody', label: 'Self-custody first' });
    }
    
    return baseOptions;
  }, [mapId]);

  // Sorted entries
  const sortedEntries = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    
    // Priority order for sorting
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    
    return [...entries].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.label || '').localeCompare(b.label || '');
        case 'name-desc':
          return (b.label || '').localeCompare(a.label || '');
        case 'date-oldest':
          return (a.lastUpdated || '').localeCompare(b.lastUpdated || '');
        case 'date-newest':
          return (b.lastUpdated || '').localeCompare(a.lastUpdated || '');
        case 'priority-low':
          return (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
        case 'priority-high':
          return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        case 'self-custody':
          // Self-custody items first, then by name
          if (a.selfCustody && !b.selfCustody) return -1;
          if (!a.selfCustody && b.selfCustody) return 1;
          return (a.label || '').localeCompare(b.label || '');
        default:
          return 0;
      }
    });
  }, [entries, sortBy]);

  // Get types and subtypes for current map
  const types = getTypesForMap(mapId);
  const subtypes = getSubtypesForType(mapId, entryType);
  const hasSubtypes = typeHasSubtypes(mapId, entryType);

  // Reset subtype when type changes
  const handleTypeChange = (newType) => {
    setEntryType(newType);
    // Reset subtype when type changes
    setEntrySubtype('not-specified');
  };

  if (!map) {
    return (
      <div className="text-center py-12" data-testid="map-not-found">
        <p className="text-muted-foreground">Map not found</p>
        <Link to="/maps" className="text-primary hover:underline mt-2 inline-block">
          Return to Maps
        </Link>
      </div>
    );
  }

  const handleSaveNotes = () => {
    updateMapNotes(mapId, notesValue);
    setIsEditingNotes(false);
    toast.success('Map notes saved');
  };

  const resetForm = () => {
    setEntryLabel('');
    setEntryType('not-specified');
    setEntrySubtype('not-specified');
    setEntryLocation('');
    setEntryPriority('medium');
    setEntryNotes('');
    setEntryFirstAction('');
    // Crypto-specific
    setSelfCustody(false);
    // Shares-specific
    setTickerSymbol('');
    setQuantity('');
    setEstimatedValue('');
    setBroker('');
    setAccountReference('');
    setPurchaseDate('');
    setCostBasis('');
    setDocumentation('');
    // Property-specific
    setPropertyAddress('');
    setOwnershipStatus('not-specified');
    setDeedLocation('');
    setMortgageProvider('');
    setSolicitorContact('');
    // Vehicle-specific
    setVehicleMake('');
    setVehicleModel('');
    setVehicleYear('');
    setVehicleRegistration('');
    setVehicleVin('');
    setVehicleOwnershipStatus('not-specified');
    setSharedOwnership(false);
    setStorageLocation('');
    setInsuranceDetails('');
    setServiceContact('');
    // Pet-specific
    setPetBreed('');
    setPetDateOfBirth('');
    setVetContact('');
    setMedicalNeeds('');
    setPetOwnershipArrangements('');
    setVerbalArrangements(false);
    setEmergencyCaregiver('');
    // Social-specific
    setPlatformName('');
    setAccountUrl('');
    setAccountDisposition('not-specified');
    // Art-specific
    setArtistCreator('');
    setYearPeriod('');
    setMediumMaterials('');
    setProvenance('');
    setAppraisedValue('');
    setAppraisalDate('');
    setArtStorageLocation('');
    setArtInsurance('');
    setAuthentication('');
    setIntendedDisposition('not-specified');
    // Physical Records-specific
    setPhysicalLocation('');
    setDocumentFormat('not-specified');
    setAccessInfo('');
    setRelatedTo('');
    setRetentionNotes('');
    // Insurance-specific
    setInsuranceProvider('');
    setPolicyReference('');
    setCoverageAmount('');
    setPremiumDetails('');
    setRenewalDate('');
    setBeneficiaries('');
    setAgentContact('');
    setPolicyDocLocation('');
    setCoverageDescription('');
    // Entry wishes
    setWishesOverride(false);
    setEntryWishesContactId('');
    setEntryWishesNotes('');
  };

  const handleOpenEntryDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setEntryLabel(entry.label || '');
      setEntryType(entry.type || 'not-specified');
      setEntrySubtype(entry.subtype || 'not-specified');
      setEntryLocation(entry.location || '');
      setEntryPriority(entry.priority || 'medium');
      setEntryNotes(entry.notes || '');
      setEntryFirstAction(entry.firstAction || '');
      // Crypto-specific
      setSelfCustody(entry.selfCustody || false);
      // Shares-specific
      setTickerSymbol(entry.tickerSymbol || '');
      setQuantity(entry.quantity || '');
      setEstimatedValue(entry.estimatedValue || '');
      setBroker(entry.broker || '');
      setAccountReference(entry.accountReference || '');
      setPurchaseDate(entry.purchaseDate || '');
      setCostBasis(entry.costBasis || '');
      setDocumentation(entry.documentation || '');
      // Property-specific
      setPropertyAddress(entry.propertyAddress || '');
      setOwnershipStatus(entry.ownershipStatus || 'not-specified');
      setDeedLocation(entry.deedLocation || '');
      setMortgageProvider(entry.mortgageProvider || '');
      setSolicitorContact(entry.solicitorContact || '');
      // Vehicle-specific
      setVehicleMake(entry.vehicleMake || '');
      setVehicleModel(entry.vehicleModel || '');
      setVehicleYear(entry.vehicleYear || '');
      setVehicleRegistration(entry.vehicleRegistration || '');
      setVehicleVin(entry.vehicleVin || '');
      setVehicleOwnershipStatus(entry.vehicleOwnershipStatus || 'not-specified');
      setSharedOwnership(entry.sharedOwnership || false);
      setStorageLocation(entry.storageLocation || '');
      setInsuranceDetails(entry.insuranceDetails || '');
      setServiceContact(entry.serviceContact || '');
      // Pet-specific
      setPetBreed(entry.petBreed || '');
      setPetDateOfBirth(entry.petDateOfBirth || '');
      setVetContact(entry.vetContact || '');
      setMedicalNeeds(entry.medicalNeeds || '');
      setPetOwnershipArrangements(entry.petOwnershipArrangements || '');
      setVerbalArrangements(entry.verbalArrangements || false);
      setEmergencyCaregiver(entry.emergencyCaregiver || '');
      // Social-specific
      setPlatformName(entry.platformName || '');
      setAccountUrl(entry.accountUrl || '');
      setAccountDisposition(entry.accountDisposition || 'not-specified');
      // Art-specific
      setArtistCreator(entry.artistCreator || '');
      setYearPeriod(entry.yearPeriod || '');
      setMediumMaterials(entry.mediumMaterials || '');
      setProvenance(entry.provenance || '');
      setAppraisedValue(entry.appraisedValue || '');
      setAppraisalDate(entry.appraisalDate || '');
      setArtStorageLocation(entry.artStorageLocation || '');
      setArtInsurance(entry.artInsurance || '');
      setAuthentication(entry.authentication || '');
      setIntendedDisposition(entry.intendedDisposition || 'not-specified');
      // Physical Records-specific
      setPhysicalLocation(entry.physicalLocation || '');
      setDocumentFormat(entry.documentFormat || 'not-specified');
      setAccessInfo(entry.accessInfo || '');
      setRelatedTo(entry.relatedTo || '');
      setRetentionNotes(entry.retentionNotes || '');
      // Insurance-specific
      setInsuranceProvider(entry.insuranceProvider || '');
      setPolicyReference(entry.policyReference || '');
      setCoverageAmount(entry.coverageAmount || '');
      setPremiumDetails(entry.premiumDetails || '');
      setRenewalDate(entry.renewalDate || '');
      setBeneficiaries(entry.beneficiaries || '');
      setAgentContact(entry.agentContact || '');
      setPolicyDocLocation(entry.policyDocLocation || '');
      setCoverageDescription(entry.coverageDescription || '');
      // Entry wishes
      setWishesOverride(entry.wishesOverride || false);
      setEntryWishesContactId(entry.wishesContactId || '');
      setEntryWishesNotes(entry.wishesNotes || '');
    } else {
      setEditingEntry(null);
      resetForm();
    }
    setIsEntryDialogOpen(true);
  };

  const handleSaveEntry = async () => {
    if (!entryLabel.trim()) {
      toast.error('Entry label is required');
      return;
    }

    const entryData = {
      label: entryLabel,
      type: entryType,
      subtype: entrySubtype,
      location: entryLocation,
      priority: entryPriority,
      notes: entryNotes,
      firstAction: entryFirstAction,
    };

    // Add crypto-specific fields
    if (mapId === 'crypto') {
      entryData.selfCustody = selfCustody;
    }

    // Add shares-specific fields
    if (mapId === 'shares') {
      entryData.tickerSymbol = tickerSymbol;
      entryData.quantity = quantity;
      entryData.estimatedValue = estimatedValue;
      entryData.broker = broker;
      entryData.accountReference = accountReference;
      entryData.purchaseDate = purchaseDate;
      entryData.costBasis = costBasis;
      entryData.documentation = documentation;
    }

    // Add property-specific fields
    if (mapId === 'property') {
      entryData.propertyAddress = propertyAddress;
      entryData.ownershipStatus = ownershipStatus;
      entryData.deedLocation = deedLocation;
      entryData.mortgageProvider = mortgageProvider;
      entryData.solicitorContact = solicitorContact;
    }

    // Add vehicle-specific fields
    if (mapId === 'vehicles') {
      entryData.vehicleMake = vehicleMake;
      entryData.vehicleModel = vehicleModel;
      entryData.vehicleYear = vehicleYear;
      entryData.vehicleRegistration = vehicleRegistration;
      entryData.vehicleVin = vehicleVin;
      entryData.vehicleOwnershipStatus = vehicleOwnershipStatus;
      entryData.sharedOwnership = sharedOwnership;
      entryData.storageLocation = storageLocation;
      entryData.insuranceDetails = insuranceDetails;
      entryData.serviceContact = serviceContact;
    }

    // Add pet-specific fields
    if (mapId === 'pets') {
      entryData.petBreed = petBreed;
      entryData.petDateOfBirth = petDateOfBirth;
      entryData.vetContact = vetContact;
      entryData.medicalNeeds = medicalNeeds;
      entryData.petOwnershipArrangements = petOwnershipArrangements;
      entryData.verbalArrangements = verbalArrangements;
      entryData.emergencyCaregiver = emergencyCaregiver;
    }

    // Add social-specific fields
    if (mapId === 'social') {
      entryData.platformName = platformName;
      entryData.accountUrl = accountUrl;
      entryData.accountDisposition = accountDisposition;
    }

    // Add art-specific fields
    if (mapId === 'art') {
      entryData.artistCreator = artistCreator;
      entryData.yearPeriod = yearPeriod;
      entryData.mediumMaterials = mediumMaterials;
      entryData.provenance = provenance;
      entryData.appraisedValue = appraisedValue;
      entryData.appraisalDate = appraisalDate;
      entryData.artStorageLocation = artStorageLocation;
      entryData.artInsurance = artInsurance;
      entryData.authentication = authentication;
      entryData.intendedDisposition = intendedDisposition;
    }

    // Add physical records-specific fields
    if (mapId === 'physical') {
      entryData.physicalLocation = physicalLocation;
      entryData.documentFormat = documentFormat;
      entryData.accessInfo = accessInfo;
      entryData.relatedTo = relatedTo;
      entryData.retentionNotes = retentionNotes;
    }

    // Add insurance-specific fields
    if (mapId === 'insurance') {
      entryData.insuranceProvider = insuranceProvider;
      entryData.policyReference = policyReference;
      entryData.coverageAmount = coverageAmount;
      entryData.premiumDetails = premiumDetails;
      entryData.renewalDate = renewalDate;
      entryData.beneficiaries = beneficiaries;
      entryData.agentContact = agentContact;
      entryData.policyDocLocation = policyDocLocation;
      entryData.coverageDescription = coverageDescription;
    }

    // Add wishes fields (all maps)
    entryData.wishesOverride = wishesOverride;
    if (wishesOverride) {
      entryData.wishesContactId = entryWishesContactId;
      entryData.wishesContactName = entryWishesContactId ? getContactById(entryWishesContactId)?.name : '';
      entryData.wishesNotes = entryWishesNotes;
    } else {
      entryData.wishesContactId = '';
      entryData.wishesContactName = '';
      entryData.wishesNotes = '';
    }

    try {
      setSaving(true);
      if (editingEntry) {
        await updateEntry(mapId, editingEntry.id, entryData);
        toast.success('Entry updated');
      } else {
        await addEntry(mapId, entryData);
        toast.success('Entry added');
      }
      setIsEntryDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await deleteEntry(mapId, entryId);
      toast.success('Entry removed');
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  const handleMarkAsReviewed = () => {
    updateMapStatus(mapId, 'updated');
    toast.success('Map marked as reviewed');
  };

  const getPriorityClasses = (priority) => {
    switch (priority) {
      case 'critical':
        return 'priority-critical';
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      default:
        return 'priority-low';
    }
  };

  // Show loading state
  if (loading.maps && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid={'map-detail-' + mapId}>
      {/* Back Link & Header */}
      <div className="space-y-4">
        <Link 
          to="/maps" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-maps"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Maps
        </Link>

        <div className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
            {map.name}
          </h1>
          <p className="text-muted-foreground text-base">
            {map.description}
          </p>
        </div>

        {/* Helper Note */}
        <div className="warning-text flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground text-sm">Do not enter passwords, private keys, seed phrases, or credentials.</p>
            <p className="mt-1">{map.helperText}</p>
          </div>
        </div>
      </div>

      {/* Map Notes / Intent Block */}
      <Card className="border border-border" data-testid="map-notes-section">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-medium font-body">
            Notes & Intent
          </CardTitle>
          {!isEditingNotes && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditingNotes(true)}
              data-testid="edit-notes-btn"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isEditingNotes ? (
            <div className="space-y-4">
              <Textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                placeholder="What would you want someone to understand about this area of your life?"
                className="min-h-[120px] resize-none"
                data-testid="notes-textarea"
              />
              <div className="flex items-center gap-2">
                <Button onClick={handleSaveNotes} data-testid="save-notes-btn">
                  Save Notes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setNotesValue(notes);
                    setIsEditingNotes(false);
                  }}
                  data-testid="cancel-notes-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              {notes ? (
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {notes}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  What would you want someone to understand about this area of your life? Click &quot;Edit&quot; to add notes.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map Default Wishes Section */}
      <Card className="border border-border" data-testid="map-wishes-section">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base font-medium font-body">
              Your Wishes (Non-Binding)
            </CardTitle>
          </div>
          {!isEditingWishes && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setShowWishesDisclaimer(true);
              }}
              data-testid="edit-wishes-btn"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                These wishes express your intent and can help executors and family understand your preferences. 
                They do not replace a legal will or binding beneficiary designations.
              </p>
            </div>
          </div>
          
          {isEditingWishes ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Default intended recipient for all entries in this map</Label>
                <ContactSelector
                  value={mapWishesContactId}
                  onSelect={setMapWishesContactId}
                  placeholder="Select contact..."
                  data-testid="map-wishes-contact"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Notes about your wishes</Label>
                <Textarea
                  value={mapWishesNotes}
                  onChange={(e) => setMapWishesNotes(e.target.value)}
                  placeholder="e.g., Split equally between children, To be managed by spouse..."
                  className="min-h-[80px] resize-none"
                  data-testid="map-wishes-notes"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => {
                    const contactName = mapWishesContactId ? getContactById(mapWishesContactId)?.name : '';
                    updateMapWishes(mapId, {
                      contactId: mapWishesContactId,
                      contactName,
                      notes: mapWishesNotes
                    });
                    setIsEditingWishes(false);
                    toast.success('Wishes saved');
                  }}
                  data-testid="save-wishes-btn"
                >
                  Save Wishes
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setMapWishesContactId(mapWishes?.contactId || '');
                    setMapWishesNotes(mapWishes?.notes || '');
                    setIsEditingWishes(false);
                  }}
                  data-testid="cancel-wishes-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {(mapWishes?.contactId || mapWishes?.notes) ? (
                <>
                  {mapWishes.contactName && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Intended for: </span>
                      <span className="font-medium">{mapWishes.contactName}</span>
                    </p>
                  )}
                  {mapWishes.notes && (
                    <p className="text-sm text-foreground whitespace-pre-wrap">{mapWishes.notes}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No default wishes set for this map. Click &quot;Edit&quot; to add your intentions.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wishes Disclaimer Dialog */}
      <Dialog open={showWishesDisclaimer} onOpenChange={setShowWishesDisclaimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Important Legal Notice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Lifely is a personal information organizer, not a legal service. 
              The wishes and intentions you record here are for <strong>informational purposes only</strong> and do not constitute:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>A legal will or testament</li>
              <li>A binding beneficiary designation</li>
              <li>Legal or financial advice</li>
              <li>A substitute for proper estate planning</li>
            </ul>
            <p className="text-sm text-muted-foreground">
              To ensure your wishes are legally binding, consult a qualified estate planning attorney in your jurisdiction.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowWishesDisclaimer(false);
                setIsEditingWishes(true);
              }}
              data-testid="acknowledge-disclaimer-btn"
            >
              I Understand, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entries Section */}
      <section className="space-y-4" data-testid="entries-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-heading font-medium text-foreground">
            Entries ({entries.length})
          </h2>
          <div className="flex items-center gap-2">
            {entries.length > 1 && (
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="sort-entries-select">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value} className="text-xs">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={() => handleOpenEntryDialog()} data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        </div>

        {entries.length === 0 ? (
          <Card className="border border-dashed border-border bg-muted/30">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No entries yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add your first entry to start mapping this area.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedEntries.map((entry) => (
              <Card 
                key={entry.id} 
                className="card-hover border border-border"
                data-testid={'entry-card-' + entry.id}
              >
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-foreground truncate">
                          {entry.label}
                        </h3>
                        {/* Crypto: Self-custody badge */}
                        {mapId === 'crypto' && entry.selfCustody && (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                            Self-custody
                          </Badge>
                        )}
                        {/* Vehicle: Shared ownership badge */}
                        {mapId === 'vehicles' && entry.sharedOwnership && (
                          <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                            Shared
                          </Badge>
                        )}
                        {/* Pet: Verbal arrangements badge */}
                        {mapId === 'pets' && entry.verbalArrangements && (
                          <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                            Verbal arrangements
                          </Badge>
                        )}
                        {/* Social: Account disposition badge */}
                        {mapId === 'social' && entry.accountDisposition && entry.accountDisposition !== 'not-specified' && (
                          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                            {entry.accountDisposition === 'memorialize' ? 'Memorialize' :
                             entry.accountDisposition === 'archive' ? 'Keep active' :
                             entry.accountDisposition === 'close' ? 'Close/Delete' :
                             entry.accountDisposition === 'transfer' ? 'Transfer' : 'Other'}
                          </Badge>
                        )}
                      </div>
                      {/* Social: Platform name */}
                      {mapId === 'social' && entry.platformName && (
                        <p className="text-xs text-primary">
                          {entry.platformName}
                        </p>
                      )}
                      {/* Social: URL */}
                      {mapId === 'social' && entry.accountUrl && (
                        <p className="text-xs text-muted-foreground truncate">
                          {entry.accountUrl}
                        </p>
                      )}
                      {/* Art: Creator and year */}
                      {mapId === 'art' && (entry.artistCreator || entry.yearPeriod) && (
                        <p className="text-xs text-primary">
                          {[entry.artistCreator, entry.yearPeriod].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* Art: Medium/Materials */}
                      {mapId === 'art' && entry.mediumMaterials && (
                        <p className="text-xs text-muted-foreground">
                          {entry.mediumMaterials}
                        </p>
                      )}
                      {/* Art: Appraised value */}
                      {mapId === 'art' && entry.appraisedValue && (
                        <p className="text-xs text-muted-foreground">
                          Value: <span className="font-medium">{entry.appraisedValue}</span>
                        </p>
                      )}
                      {/* Shares: Ticker Symbol */}
                      {mapId === 'shares' && entry.tickerSymbol && (
                        <p className="text-xs font-mono text-primary">
                          {entry.tickerSymbol}
                        </p>
                      )}
                      {/* Vehicle: Make/Model/Year */}
                      {mapId === 'vehicles' && (entry.vehicleMake || entry.vehicleModel || entry.vehicleYear) && (
                        <p className="text-xs text-primary">
                          {[entry.vehicleYear, entry.vehicleMake, entry.vehicleModel].filter(Boolean).join(' ')}
                        </p>
                      )}
                      {/* Pet: Breed */}
                      {mapId === 'pets' && entry.petBreed && (
                        <p className="text-xs text-primary">
                          {entry.petBreed}
                        </p>
                      )}
                      {/* Property: Address */}
                      {mapId === 'property' && entry.propertyAddress && (
                        <p className="text-xs text-muted-foreground">
                          {entry.propertyAddress}
                        </p>
                      )}
                      {/* Insurance: Provider */}
                      {mapId === 'insurance' && entry.insuranceProvider && (
                        <p className="text-xs text-primary">
                          {entry.insuranceProvider}
                        </p>
                      )}
                      {/* Insurance: Coverage amount */}
                      {mapId === 'insurance' && entry.coverageAmount && (
                        <p className="text-xs text-muted-foreground">
                          Coverage: <span className="font-medium">{entry.coverageAmount}</span>
                        </p>
                      )}
                      {/* Physical: Location */}
                      {mapId === 'physical' && entry.physicalLocation && (
                        <p className="text-xs text-muted-foreground">
                          📍 {entry.physicalLocation}
                        </p>
                      )}
                      {/* Physical: Document format badge */}
                      {mapId === 'physical' && entry.documentFormat && entry.documentFormat !== 'not-specified' && (
                        <Badge variant="outline" className="text-xs">
                          {entry.documentFormat === 'original' ? 'Original' :
                           entry.documentFormat === 'certified-copy' ? 'Certified copy' :
                           entry.documentFormat === 'photocopy' ? 'Photocopy' :
                           entry.documentFormat === 'digital-scan' ? 'Digital scan' :
                           entry.documentFormat === 'both' ? 'Physical + Digital' : entry.documentFormat}
                        </Badge>
                      )}
                      {entry.type && entry.type !== 'not-specified' && (
                        <p className="text-xs text-primary">
                          {getTypeLabel(mapId, entry.type)}
                          {entry.subtype && entry.subtype !== 'not-specified' && (
                            <span className="text-muted-foreground"> / {getSubtypeLabel(mapId, entry.type, entry.subtype)}</span>
                          )}
                        </p>
                      )}
                      {/* Generic location - only show if not property/vehicle/pet/social/art/physical/insurance specific */}
                      {entry.location && !['property', 'vehicles', 'pets', 'social', 'art', 'physical', 'insurance'].includes(mapId) && (
                        <p className="text-sm text-muted-foreground truncate">
                          {entry.location}
                        </p>
                      )}
                      {/* Vehicle: Storage location */}
                      {mapId === 'vehicles' && entry.storageLocation && (
                        <p className="text-xs text-muted-foreground truncate">
                          📍 {entry.storageLocation}
                        </p>
                      )}
                      {/* Art: Storage location */}
                      {mapId === 'art' && entry.artStorageLocation && (
                        <p className="text-xs text-muted-foreground truncate">
                          📍 {entry.artStorageLocation}
                        </p>
                      )}
                      {/* Pet: Emergency caregiver */}
                      {mapId === 'pets' && entry.emergencyCaregiver && (
                        <p className="text-xs text-muted-foreground truncate">
                          Emergency: {entry.emergencyCaregiver}
                        </p>
                      )}
                      {/* Insurance: Renewal date */}
                      {mapId === 'insurance' && entry.renewalDate && (
                        <p className="text-xs text-muted-foreground truncate">
                          Renews: {entry.renewalDate}
                        </p>
                      )}
                      {/* Insurance: Beneficiaries */}
                      {mapId === 'insurance' && entry.beneficiaries && (
                        <p className="text-xs text-muted-foreground truncate">
                          Beneficiary: {entry.beneficiaries}
                        </p>
                      )}
                      {/* Physical: Related to */}
                      {mapId === 'physical' && entry.relatedTo && (
                        <p className="text-xs text-muted-foreground truncate">
                          Related: {entry.relatedTo}
                        </p>
                      )}
                      {/* Shares: Quantity and Value */}
                      {mapId === 'shares' && (entry.quantity || entry.estimatedValue) && (
                        <p className="text-xs text-muted-foreground">
                          {entry.quantity && <span>{entry.quantity}</span>}
                          {entry.quantity && entry.estimatedValue && <span> · </span>}
                          {entry.estimatedValue && <span className="font-medium">{entry.estimatedValue}</span>}
                        </p>
                      )}
                    </div>
                    <Badge className={'text-xs ' + getPriorityClasses(entry.priority)}>
                      {PRIORITY_LABELS[entry.priority]}
                    </Badge>
                  </div>

                  {entry.notes && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {entry.notes}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Updated: {entry.lastUpdated}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenEntryDialog(entry)}
                        data-testid={'edit-entry-' + entry.id}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={'delete-entry-' + entry.id}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Entry Dialog */}
      <Dialog open={isEntryDialogOpen} onOpenChange={setIsEntryDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingEntry ? 'Edit Entry' : 'Add Entry'}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Document what exists and where it&apos;s managed—not credentials or secrets.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="warning-text text-xs">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-2" />
              Lifely is not a vault. Do not enter passwords, private keys, seed phrases, or secret recovery codes.
            </div>

            {/* Entry Label - Dynamic label based on map type */}
            <div className="space-y-2">
              <Label htmlFor="entry-label">
                {mapId === 'shares' ? 'Investment Name / Label *' : 
                 mapId === 'property' ? 'Property Name / Label *' :
                 mapId === 'vehicles' ? 'Vehicle Name / Label *' :
                 mapId === 'pets' ? 'Pet Name / Identifier *' :
                 mapId === 'social' ? 'Account Name / Username *' :
                 mapId === 'art' ? 'Item Name / Title *' :
                 mapId === 'physical' ? 'Document / Record Name *' :
                 mapId === 'insurance' ? 'Policy Name / Description *' :
                 'Entry Label *'}
              </Label>
              <Input
                id="entry-label"
                value={entryLabel}
                onChange={(e) => setEntryLabel(e.target.value)}
                placeholder={
                  mapId === 'shares' ? 'e.g., Apple Inc., Vanguard S&P 500 ETF' : 
                  mapId === 'property' ? 'e.g., Family Home, Beach House, Rental Property' :
                  mapId === 'vehicles' ? 'e.g., Daily Driver, Classic Mustang, Family Boat' :
                  mapId === 'pets' ? 'e.g., Max, Bella, Mr. Whiskers' :
                  mapId === 'social' ? 'e.g., @johndoe, john.doe@email.com' :
                  mapId === 'art' ? 'e.g., Sunset over Mountains, Grandmother\'s Ring, 1965 Rolex Submariner' :
                  mapId === 'physical' ? 'e.g., Birth Certificate, Property Deed, Tax Returns 2020-2023' :
                  mapId === 'insurance' ? 'e.g., Home Insurance, Term Life Policy, Auto Insurance' :
                  'e.g., Primary Savings Account'
                }
                data-testid="entry-label-input"
              />
            </div>

            {/* Insurance-specific: Insurance Provider */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="insurance-provider">Insurance Provider / Company</Label>
                <Input
                  id="insurance-provider"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  placeholder="e.g., State Farm, Allstate, MetLife, Prudential"
                  data-testid="insurance-provider-input"
                />
              </div>
            )}

            {/* Insurance-specific: Policy Reference */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="policy-reference">Policy Reference</Label>
                <Input
                  id="policy-reference"
                  value={policyReference}
                  onChange={(e) => setPolicyReference(e.target.value)}
                  placeholder="e.g., Policy ending in 4521, Group policy through employer"
                  data-testid="policy-reference-input"
                />
              </div>
            )}

            {/* Physical-specific: Physical Location */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="physical-location">Physical Location</Label>
                <Input
                  id="physical-location"
                  value={physicalLocation}
                  onChange={(e) => setPhysicalLocation(e.target.value)}
                  placeholder="e.g., Filing cabinet in home office, Safe deposit box at bank, Home safe"
                  data-testid="physical-location-input"
                />
              </div>
            )}

            {/* Physical-specific: Document Format */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="document-format">Document Format</Label>
                <Select value={documentFormat} onValueChange={setDocumentFormat}>
                  <SelectTrigger id="document-format" data-testid="document-format-select">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="original">Original document</SelectItem>
                    <SelectItem value="certified-copy">Certified copy</SelectItem>
                    <SelectItem value="photocopy">Photocopy</SelectItem>
                    <SelectItem value="digital-scan">Digital scan</SelectItem>
                    <SelectItem value="both">Both physical and digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Social-specific: Platform Name */}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="platform-name">Platform Name</Label>
                <Input
                  id="platform-name"
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  placeholder="e.g., Facebook, Instagram, LinkedIn, Gmail, Dropbox"
                  data-testid="platform-name-input"
                />
              </div>
            )}

            {/* Social-specific: Location / URL */}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="account-url">Location / URL</Label>
                <Input
                  id="account-url"
                  value={accountUrl}
                  onChange={(e) => setAccountUrl(e.target.value)}
                  placeholder="e.g., facebook.com/johndoe, linkedin.com/in/johndoe"
                  data-testid="account-url-input"
                />
              </div>
            )}

            {/* Art-specific: Creator / Artist / Maker */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="artist-creator">Creator / Artist / Maker</Label>
                <Input
                  id="artist-creator"
                  value={artistCreator}
                  onChange={(e) => setArtistCreator(e.target.value)}
                  placeholder="e.g., Claude Monet, Tiffany & Co., Rolex, Unknown craftsman"
                  data-testid="artist-creator-input"
                />
              </div>
            )}

            {/* Art-specific: Year / Period */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="year-period">Year / Period</Label>
                <Input
                  id="year-period"
                  value={yearPeriod}
                  onChange={(e) => setYearPeriod(e.target.value)}
                  placeholder="e.g., 1889, Victorian era, 1960s, Circa 1920"
                  data-testid="year-period-input"
                />
              </div>
            )}

            {/* Art-specific: Medium / Materials */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="medium-materials">Medium / Materials</Label>
                <Input
                  id="medium-materials"
                  value={mediumMaterials}
                  onChange={(e) => setMediumMaterials(e.target.value)}
                  placeholder="e.g., Oil on canvas, 18k gold with diamonds, Sterling silver, Bronze sculpture"
                  data-testid="medium-materials-input"
                />
              </div>
            )}

            {/* Property-specific: Property Address */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="property-address">Property Address</Label>
                <Input
                  id="property-address"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  placeholder="e.g., 123 Main Street, City, State, ZIP"
                  data-testid="property-address-input"
                />
              </div>
            )}

            {/* Pet-specific: Breed / Species */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="pet-breed">Breed / Species</Label>
                <Input
                  id="pet-breed"
                  value={petBreed}
                  onChange={(e) => setPetBreed(e.target.value)}
                  placeholder="e.g., Golden Retriever, Domestic Shorthair, Cockatiel"
                  data-testid="pet-breed-input"
                />
              </div>
            )}

            {/* Pet-specific: Date of Birth / Age */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="pet-dob">Date of Birth / Age</Label>
                <Input
                  id="pet-dob"
                  value={petDateOfBirth}
                  onChange={(e) => setPetDateOfBirth(e.target.value)}
                  placeholder="e.g., March 2019, ~5 years old"
                  data-testid="pet-dob-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Make */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-make">Make</Label>
                <Input
                  id="vehicle-make"
                  value={vehicleMake}
                  onChange={(e) => setVehicleMake(e.target.value)}
                  placeholder="e.g., Ford, Toyota, Porsche, Beneteau"
                  data-testid="vehicle-make-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Model */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-model">Model</Label>
                <Input
                  id="vehicle-model"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  placeholder="e.g., Mustang GT, Camry, 911 Carrera, Oceanis 40"
                  data-testid="vehicle-model-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Year */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-year">Year</Label>
                <Input
                  id="vehicle-year"
                  value={vehicleYear}
                  onChange={(e) => setVehicleYear(e.target.value)}
                  placeholder="e.g., 2022, 1967"
                  data-testid="vehicle-year-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Registration */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-registration">Registration / License Plate</Label>
                <Input
                  id="vehicle-registration"
                  value={vehicleRegistration}
                  onChange={(e) => setVehicleRegistration(e.target.value)}
                  placeholder="e.g., ABC 123, Boat registration number"
                  data-testid="vehicle-registration-input"
                />
              </div>
            )}

            {/* Vehicle-specific: VIN/Chassis */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-vin">VIN / Chassis / Hull Number</Label>
                <Input
                  id="vehicle-vin"
                  value={vehicleVin}
                  onChange={(e) => setVehicleVin(e.target.value)}
                  placeholder="e.g., 1HGBH41JXMN109186"
                  data-testid="vehicle-vin-input"
                />
              </div>
            )}

            {/* Shares-specific: Ticker Symbol / ISIN */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="ticker-symbol">Ticker Symbol / ISIN</Label>
                <Input
                  id="ticker-symbol"
                  value={tickerSymbol}
                  onChange={(e) => setTickerSymbol(e.target.value)}
                  placeholder="e.g., AAPL, MSFT, VOO, GOOGL, US0378331005"
                  data-testid="ticker-symbol-input"
                />
              </div>
            )}

            {/* Type */}
            <div className="space-y-2">
              <Label htmlFor="entry-type">Type</Label>
              <Select value={entryType} onValueChange={handleTypeChange}>
                <SelectTrigger id="entry-type" data-testid="entry-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subtype - only show if type has subtypes */}
            {hasSubtypes && subtypes.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="entry-subtype">Subtype</Label>
                <Select value={entrySubtype} onValueChange={setEntrySubtype}>
                  <SelectTrigger id="entry-subtype" data-testid="entry-subtype-select">
                    <SelectValue placeholder="Select subtype" />
                  </SelectTrigger>
                  <SelectContent>
                    {subtypes.map((subtype) => (
                      <SelectItem key={subtype.value} value={subtype.value}>
                        {subtype.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Property-specific: Ownership Status */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="ownership-status">Ownership Status</Label>
                <Select value={ownershipStatus} onValueChange={setOwnershipStatus}>
                  <SelectTrigger id="ownership-status" data-testid="ownership-status-select">
                    <SelectValue placeholder="Select ownership status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="owned-outright">Owned outright</SelectItem>
                    <SelectItem value="mortgaged">Mortgaged</SelectItem>
                    <SelectItem value="joint-ownership">Joint ownership</SelectItem>
                    <SelectItem value="leasehold">Leasehold</SelectItem>
                    <SelectItem value="rented-to-tenants">Rented to tenants</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Vehicle-specific: Ownership Status */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="vehicle-ownership-status">Ownership Status</Label>
                <Select value={vehicleOwnershipStatus} onValueChange={setVehicleOwnershipStatus}>
                  <SelectTrigger id="vehicle-ownership-status" data-testid="vehicle-ownership-status-select">
                    <SelectValue placeholder="Select ownership status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="owned-outright">Owned outright</SelectItem>
                    <SelectItem value="financed">Financed</SelectItem>
                    <SelectItem value="leased">Leased</SelectItem>
                    <SelectItem value="shared-ownership">Shared ownership / Syndicate</SelectItem>
                    <SelectItem value="investment-vehicle">Investment vehicle</SelectItem>
                    <SelectItem value="company-owned">Company owned</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Social-specific: What should happen to this account? */}
            {mapId === 'social' && (
              <div className="space-y-2">
                <Label htmlFor="account-disposition">What should happen to this account?</Label>
                <Select value={accountDisposition} onValueChange={setAccountDisposition}>
                  <SelectTrigger id="account-disposition" data-testid="account-disposition-select">
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="memorialize">Memorialize (keep visible but locked)</SelectItem>
                    <SelectItem value="archive">Archive / Keep active</SelectItem>
                    <SelectItem value="close">Close / Delete</SelectItem>
                    <SelectItem value="transfer">Transfer to someone</SelectItem>
                    <SelectItem value="other">Other (explain in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Art-specific: Provenance / History */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="provenance">Provenance / History</Label>
                <Textarea
                  id="provenance"
                  value={provenance}
                  onChange={(e) => setProvenance(e.target.value)}
                  placeholder="e.g., Purchased at Christie's 2015, Inherited from grandmother, Family heirloom since 1920"
                  className="min-h-[60px] resize-none"
                  data-testid="provenance-input"
                />
              </div>
            )}

            {/* Art-specific: Appraised Value */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="appraised-value">Appraised Value</Label>
                <Input
                  id="appraised-value"
                  value={appraisedValue}
                  onChange={(e) => setAppraisedValue(e.target.value)}
                  placeholder="e.g., $15,000, £8,500, Estimated $5,000-7,000"
                  data-testid="appraised-value-input"
                />
              </div>
            )}

            {/* Art-specific: Appraisal Date */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="appraisal-date">Appraisal Date</Label>
                <Input
                  id="appraisal-date"
                  value={appraisalDate}
                  onChange={(e) => setAppraisalDate(e.target.value)}
                  placeholder="e.g., March 2023, Never appraised"
                  data-testid="appraisal-date-input"
                />
              </div>
            )}

            {/* Art-specific: Storage Location */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-storage-location">Storage Location</Label>
                <Input
                  id="art-storage-location"
                  value={artStorageLocation}
                  onChange={(e) => setArtStorageLocation(e.target.value)}
                  placeholder="e.g., Displayed in living room, Bank safe deposit box, Climate-controlled storage"
                  data-testid="art-storage-location-input"
                />
              </div>
            )}

            {/* Art-specific: Insurance */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="art-insurance">Insurance Details</Label>
                <Input
                  id="art-insurance"
                  value={artInsurance}
                  onChange={(e) => setArtInsurance(e.target.value)}
                  placeholder="e.g., Covered under home policy, Separate rider with Chubb, Not insured"
                  data-testid="art-insurance-input"
                />
              </div>
            )}

            {/* Art-specific: Authentication / Documentation */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="authentication">Authentication / Documentation</Label>
                <Textarea
                  id="authentication"
                  value={authentication}
                  onChange={(e) => setAuthentication(e.target.value)}
                  placeholder="e.g., Certificate of authenticity from gallery, GIA certificate #12345, Original receipt in safe"
                  className="min-h-[60px] resize-none"
                  data-testid="authentication-input"
                />
              </div>
            )}

            {/* Art-specific: Intended Disposition */}
            {mapId === 'art' && (
              <div className="space-y-2">
                <Label htmlFor="intended-disposition">Intended Disposition</Label>
                <Select value={intendedDisposition} onValueChange={setIntendedDisposition}>
                  <SelectTrigger id="intended-disposition" data-testid="intended-disposition-select">
                    <SelectValue placeholder="What should happen to this item?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not specified</SelectItem>
                    <SelectItem value="keep-in-family">Keep in family</SelectItem>
                    <SelectItem value="gift-to-specific">Gift to specific person</SelectItem>
                    <SelectItem value="sell-auction">Sell at auction</SelectItem>
                    <SelectItem value="sell-dealer">Sell through dealer</SelectItem>
                    <SelectItem value="donate-museum">Donate to museum/institution</SelectItem>
                    <SelectItem value="donate-charity">Donate to charity</SelectItem>
                    <SelectItem value="other">Other (explain in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Physical-specific: Who has access */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="access-info">Who Has Access / Knows Location</Label>
                <Input
                  id="access-info"
                  value={accessInfo}
                  onChange={(e) => setAccessInfo(e.target.value)}
                  placeholder="e.g., Spouse has key, Lawyer has copy, Only I know combination"
                  data-testid="access-info-input"
                />
              </div>
            )}

            {/* Physical-specific: Related To */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="related-to">Related To (asset/property)</Label>
                <Input
                  id="related-to"
                  value={relatedTo}
                  onChange={(e) => setRelatedTo(e.target.value)}
                  placeholder="e.g., Family Home deed, 2019 Toyota, Life insurance policy"
                  data-testid="related-to-input"
                />
              </div>
            )}

            {/* Physical-specific: Retention Notes */}
            {mapId === 'physical' && (
              <div className="space-y-2">
                <Label htmlFor="retention-notes">Retention Notes</Label>
                <Textarea
                  id="retention-notes"
                  value={retentionNotes}
                  onChange={(e) => setRetentionNotes(e.target.value)}
                  placeholder="e.g., Keep forever, Destroy after 7 years, Keep until property sold"
                  className="min-h-[60px] resize-none"
                  data-testid="retention-notes-input"
                />
              </div>
            )}

            {/* Insurance-specific: Coverage Amount */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="coverage-amount">Coverage Amount / Sum Insured</Label>
                <Input
                  id="coverage-amount"
                  value={coverageAmount}
                  onChange={(e) => setCoverageAmount(e.target.value)}
                  placeholder="e.g., $500,000, £250,000 death benefit, $100k/$300k liability"
                  data-testid="coverage-amount-input"
                />
              </div>
            )}

            {/* Insurance-specific: Premium Details */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="premium-details">Premium Details</Label>
                <Input
                  id="premium-details"
                  value={premiumDetails}
                  onChange={(e) => setPremiumDetails(e.target.value)}
                  placeholder="e.g., $150/month, $1,200/year, Paid through employer"
                  data-testid="premium-details-input"
                />
              </div>
            )}

            {/* Insurance-specific: Renewal Date */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="renewal-date">Renewal Date</Label>
                <Input
                  id="renewal-date"
                  value={renewalDate}
                  onChange={(e) => setRenewalDate(e.target.value)}
                  placeholder="e.g., March 15 annually, Auto-renews, Term ends 2035"
                  data-testid="renewal-date-input"
                />
              </div>
            )}

            {/* Insurance-specific: Beneficiaries */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="beneficiaries">Beneficiaries</Label>
                <Input
                  id="beneficiaries"
                  value={beneficiaries}
                  onChange={(e) => setBeneficiaries(e.target.value)}
                  placeholder="e.g., Spouse primary, Children contingent, Estate"
                  data-testid="beneficiaries-input"
                />
              </div>
            )}

            {/* Insurance-specific: Agent / Broker Contact */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="agent-contact">Agent / Broker Contact</Label>
                <Input
                  id="agent-contact"
                  value={agentContact}
                  onChange={(e) => setAgentContact(e.target.value)}
                  placeholder="e.g., John Smith, ABC Insurance Agency (555-1234)"
                  data-testid="agent-contact-input"
                />
              </div>
            )}

            {/* Insurance-specific: Policy Documents Location */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="policy-doc-location">Policy Documents Location</Label>
                <Input
                  id="policy-doc-location"
                  value={policyDocLocation}
                  onChange={(e) => setPolicyDocLocation(e.target.value)}
                  placeholder="e.g., Filing cabinet, Email archive, Online portal"
                  data-testid="policy-doc-location-input"
                />
              </div>
            )}

            {/* Insurance-specific: What This Policy Covers */}
            {mapId === 'insurance' && (
              <div className="space-y-2">
                <Label htmlFor="coverage-description">What This Policy Covers</Label>
                <Textarea
                  id="coverage-description"
                  value={coverageDescription}
                  onChange={(e) => setCoverageDescription(e.target.value)}
                  placeholder="e.g., Primary residence and contents, All vehicles, Income replacement for 20 years"
                  className="min-h-[60px] resize-none"
                  data-testid="coverage-description-input"
                />
              </div>
            )}

            {/* Crypto-specific: Self-custody checkbox */}
            {mapId === 'crypto' && (
              <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="self-custody"
                  checked={selfCustody}
                  onCheckedChange={setSelfCustody}
                  data-testid="self-custody-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="self-custody" className="font-medium cursor-pointer">
                    Self-custody
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This asset is held in my direct control (not on an exchange or third-party platform)
                  </p>
                </div>
              </div>
            )}

            {/* Vehicle-specific: Shared ownership checkbox */}
            {mapId === 'vehicles' && (
              <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="shared-ownership"
                  checked={sharedOwnership}
                  onCheckedChange={setSharedOwnership}
                  data-testid="shared-ownership-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="shared-ownership" className="font-medium cursor-pointer">
                    Shared ownership or verbal arrangements
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This vehicle has shared ownership, syndicate arrangements, or verbal agreements (classic car clubs, race car partnerships, etc.)
                  </p>
                </div>
              </div>
            )}

            {/* Pet-specific: Verbal arrangements checkbox */}
            {mapId === 'pets' && (
              <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50 border border-border">
                <Checkbox
                  id="verbal-arrangements"
                  checked={verbalArrangements}
                  onCheckedChange={setVerbalArrangements}
                  data-testid="verbal-arrangements-checkbox"
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="verbal-arrangements" className="font-medium cursor-pointer">
                    Verbal arrangements
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This animal&apos;s care or ownership includes informal or verbal agreements (not documented in writing)
                  </p>
                </div>
              </div>
            )}

            {/* Property-specific: Deed / Title Location */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="deed-location">Deed / Title Location</Label>
                <Input
                  id="deed-location"
                  value={deedLocation}
                  onChange={(e) => setDeedLocation(e.target.value)}
                  placeholder="e.g., Safe deposit box at First National Bank, With solicitor"
                  data-testid="deed-location-input"
                />
              </div>
            )}

            {/* Property-specific: Mortgage Provider / Lender */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="mortgage-provider">Mortgage Provider / Lender</Label>
                <Input
                  id="mortgage-provider"
                  value={mortgageProvider}
                  onChange={(e) => setMortgageProvider(e.target.value)}
                  placeholder="e.g., Wells Fargo, Nationwide Building Society"
                  data-testid="mortgage-provider-input"
                />
              </div>
            )}

            {/* Property-specific: Solicitor / Legal Contact */}
            {mapId === 'property' && (
              <div className="space-y-2">
                <Label htmlFor="solicitor-contact">Solicitor / Legal Contact</Label>
                <Input
                  id="solicitor-contact"
                  value={solicitorContact}
                  onChange={(e) => setSolicitorContact(e.target.value)}
                  placeholder="e.g., Smith & Associates, Contact: John Smith"
                  data-testid="solicitor-contact-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Storage Location */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="storage-location">Storage Location</Label>
                <Input
                  id="storage-location"
                  value={storageLocation}
                  onChange={(e) => setStorageLocation(e.target.value)}
                  placeholder="e.g., Home garage, Classic car storage, Marina berth A-15, Hangar 7"
                  data-testid="storage-location-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Insurance Details */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="insurance-details">Insurance Details</Label>
                <Input
                  id="insurance-details"
                  value={insuranceDetails}
                  onChange={(e) => setInsuranceDetails(e.target.value)}
                  placeholder="e.g., State Farm, Policy through Hagerty for classic coverage"
                  data-testid="insurance-details-input"
                />
              </div>
            )}

            {/* Vehicle-specific: Service & Maintenance Contact */}
            {mapId === 'vehicles' && (
              <div className="space-y-2">
                <Label htmlFor="service-contact">Service & Maintenance Contact</Label>
                <Input
                  id="service-contact"
                  value={serviceContact}
                  onChange={(e) => setServiceContact(e.target.value)}
                  placeholder="e.g., Joe's Auto Shop, Marina service department"
                  data-testid="service-contact-input"
                />
              </div>
            )}

            {/* Pet-specific: Veterinary / Care Contact */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="vet-contact">Veterinary / Care Contact</Label>
                <Input
                  id="vet-contact"
                  value={vetContact}
                  onChange={(e) => setVetContact(e.target.value)}
                  placeholder="e.g., Happy Paws Veterinary Clinic, Dr. Sarah Johnson"
                  data-testid="vet-contact-input"
                />
              </div>
            )}

            {/* Pet-specific: Medical / Care Needs */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="medical-needs">Medical / Care Needs</Label>
                <Textarea
                  id="medical-needs"
                  value={medicalNeeds}
                  onChange={(e) => setMedicalNeeds(e.target.value)}
                  placeholder="e.g., Daily medication for thyroid, Special diet required, Anxiety around strangers"
                  className="min-h-[60px] resize-none"
                  data-testid="medical-needs-input"
                />
              </div>
            )}

            {/* Pet-specific: Ownership & Arrangements */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="pet-ownership">Ownership & Arrangements</Label>
                <Textarea
                  id="pet-ownership"
                  value={petOwnershipArrangements}
                  onChange={(e) => setPetOwnershipArrangements(e.target.value)}
                  placeholder="e.g., Registered in my name, Shared custody with ex-partner, Rescue adoption papers on file"
                  className="min-h-[60px] resize-none"
                  data-testid="pet-ownership-input"
                />
              </div>
            )}

            {/* Pet-specific: Emergency / Alternate Caregiver */}
            {mapId === 'pets' && (
              <div className="space-y-2">
                <Label htmlFor="emergency-caregiver">Emergency / Alternate Caregiver</Label>
                <Input
                  id="emergency-caregiver"
                  value={emergencyCaregiver}
                  onChange={(e) => setEmergencyCaregiver(e.target.value)}
                  placeholder="e.g., Sister Jane (555-1234), Neighbor Bob has spare key"
                  data-testid="emergency-caregiver-input"
                />
              </div>
            )}

            {/* Shares-specific: Quantity / Units */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity / Units</Label>
                <Input
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g., 100 shares, 50.5 units"
                  data-testid="quantity-input"
                />
              </div>
            )}

            {/* Shares-specific: Estimated Value */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="estimated-value">Estimated Value</Label>
                <Input
                  id="estimated-value"
                  value={estimatedValue}
                  onChange={(e) => setEstimatedValue(e.target.value)}
                  placeholder="e.g., $15,000, £10,000"
                  data-testid="estimated-value-input"
                />
              </div>
            )}

            {/* Location / System - Dynamic label based on map type (hide for property/vehicles/pets) */}
            {!['property', 'vehicles', 'pets'].includes(mapId) && (
              <div className="space-y-2">
                <Label htmlFor="entry-location">
                  {mapId === 'shares' ? 'Broker / Platform / Provider' : 'Location / System'}
                </Label>
                <Input
                  id="entry-location"
                  value={entryLocation}
                  onChange={(e) => setEntryLocation(e.target.value)}
                  placeholder={mapId === 'shares' ? 'e.g., Vanguard, Fidelity, Interactive Brokers' : 'e.g., First National Bank'}
                  data-testid="entry-location-input"
                />
              </div>
            )}

            {/* Shares-specific: Account Reference / Number */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="account-reference">Account Reference / Number</Label>
                <Input
                  id="account-reference"
                  value={accountReference}
                  onChange={(e) => setAccountReference(e.target.value)}
                  placeholder="e.g., Account ending in 1234"
                  data-testid="account-reference-input"
                />
              </div>
            )}

            {/* Shares-specific: Purchase Date */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="purchase-date">Purchase Date</Label>
                <Input
                  id="purchase-date"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  data-testid="purchase-date-input"
                />
              </div>
            )}

            {/* Shares-specific: Cost Basis */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="cost-basis">Cost Basis</Label>
                <Input
                  id="cost-basis"
                  value={costBasis}
                  onChange={(e) => setCostBasis(e.target.value)}
                  placeholder="e.g., $12,500 total, $125/share"
                  data-testid="cost-basis-input"
                />
              </div>
            )}

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="entry-priority">Priority</Label>
              <Select value={entryPriority} onValueChange={setEntryPriority}>
                <SelectTrigger id="entry-priority" data-testid="entry-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      <div className="flex flex-col">
                        <span>{level.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes / Context */}
            <div className="space-y-2">
              <Label htmlFor="entry-notes">
                {mapId === 'pets' ? 'Notes / Context (use for additional details)' : 'Notes / Context'}
              </Label>
              <Textarea
                id="entry-notes"
                value={entryNotes}
                onChange={(e) => setEntryNotes(e.target.value)}
                placeholder={mapId === 'pets' ? 'Any additional details about care routine, preferences, quirks...' : 'Any relevant context...'}
                className="min-h-[80px] resize-none"
                data-testid="entry-notes-input"
              />
            </div>

            {/* Shares-specific: Documentation & Certificates */}
            {mapId === 'shares' && (
              <div className="space-y-2">
                <Label htmlFor="documentation">Documentation & Certificates</Label>
                <Textarea
                  id="documentation"
                  value={documentation}
                  onChange={(e) => setDocumentation(e.target.value)}
                  placeholder="e.g., Physical certificates in safe deposit box, Digital statements in email archive"
                  className="min-h-[60px] resize-none"
                  data-testid="documentation-input"
                />
              </div>
            )}

            {/* What someone should do first */}
            <div className="space-y-2">
              <Label htmlFor="entry-first-action">What someone should do first (optional)</Label>
              <Input
                id="entry-first-action"
                value={entryFirstAction}
                onChange={(e) => setEntryFirstAction(e.target.value)}
                placeholder={
                  mapId === 'pets' ? 'e.g., Contact sister Jane immediately, she knows the routine' :
                  mapId === 'vehicles' ? 'e.g., Contact insurance company, keys in kitchen drawer' :
                  mapId === 'property' ? 'e.g., Contact solicitor, mortgage statements in filing cabinet' :
                  'e.g., Contact the branch manager'
                }
                data-testid="entry-first-action-input"
              />
            </div>

            {/* Entry-level wishes (Non-binding) */}
            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Your Wishes for This Entry (Non-Binding)</Label>
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  These wishes do not replace a legal will or beneficiary designations.
                </p>
              </div>

              <RadioGroup 
                value={wishesOverride ? 'override' : 'default'} 
                onValueChange={(v) => setWishesOverride(v === 'override')}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="default" id="wishes-default" />
                  <Label htmlFor="wishes-default" className="text-sm font-normal cursor-pointer">
                    Use map default
                    {mapWishes?.contactName && (
                      <span className="text-muted-foreground ml-1">
                        ({mapWishes.contactName})
                      </span>
                    )}
                    {!mapWishes?.contactId && !mapWishes?.notes && (
                      <span className="text-muted-foreground ml-1">(not set)</span>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="override" id="wishes-override" />
                  <Label htmlFor="wishes-override" className="text-sm font-normal cursor-pointer">
                    Override for this entry
                  </Label>
                </div>
              </RadioGroup>

              {wishesOverride && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Intended for</Label>
                    <ContactSelector
                      value={entryWishesContactId}
                      onSelect={setEntryWishesContactId}
                      placeholder="Select contact..."
                      data-testid="entry-wishes-contact"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Textarea
                      value={entryWishesNotes}
                      onChange={(e) => setEntryWishesNotes(e.target.value)}
                      placeholder="e.g., Held in trust until age 25..."
                      className="min-h-[60px] resize-none text-sm"
                      data-testid="entry-wishes-notes"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsEntryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} data-testid="save-entry-btn">
              {editingEntry ? 'Update Entry' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapDetail;
